// PatientSelector.js - Sökbar patientväljare för inspelningsvy
import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../apiClient';
import { createPatient } from '../Calendar/calendarApi';
import './PatientSelector.css';

const PatientSelector = ({
  selectedPatient,
  onPatientSelect,
  onClear,
  placeholder = 'Sök patient...',
  disabled = false,
  showAddButton = true,
  compact = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState(null);
  const [newPatient, setNewPatient] = useState({
    personnummer: '',
    firstname: '',
    lastname: '',
    email: '',
    phone: ''
  });
  const wrapperRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Sök patienter med debounce
  const searchPatients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setPatients([]);
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get('/api/calendar/patients', {
        params: { search: query, limit: 10 }
      });
      setPatients(response.data.patients || []);
    } catch (error) {
      console.error('Error searching patients:', error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchPatients(searchQuery);
      }, 300);
    } else {
      setPatients([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, searchPatients]);

  // Stäng dropdown när man klickar utanför
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (patient) => {
    onPatientSelect(patient);
    setSearchQuery('');
    setPatients([]);
    setShowDropdown(false);
  };

  const handleClear = () => {
    onClear?.();
    setSearchQuery('');
    setPatients([]);
  };

  // Hantera skapande av ny patient
  const handleOpenAddModal = () => {
    setShowDropdown(false);
    setShowAddModal(true);
    setAddError(null);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddError(null);
    setNewPatient({
      personnummer: '',
      firstname: '',
      lastname: '',
      email: '',
      phone: ''
    });
  };

  const handleCreatePatient = async (e) => {
    e.preventDefault();

    if (!newPatient.personnummer || !newPatient.firstname || !newPatient.lastname) {
      setAddError('Personnummer, förnamn och efternamn krävs');
      return;
    }

    try {
      setSaving(true);
      setAddError(null);
      const patient = await createPatient(newPatient);

      // Välj den nyskapade patienten direkt
      onPatientSelect(patient);

      // Stäng modal och återställ
      handleCloseAddModal();
    } catch (err) {
      setAddError(err.response?.data?.error || 'Kunde inte skapa patient');
    } finally {
      setSaving(false);
    }
  };

  const formatPersonnummer = (pnr) => {
    if (!pnr) return '';
    // Formatera som XXXXXX-XXXX
    if (pnr.length === 12 && !pnr.includes('-')) {
      return pnr.slice(0, 8) + '-' + pnr.slice(8);
    }
    return pnr;
  };

  // Om en patient är vald - visa valda patienten
  if (selectedPatient) {
    return (
      <div className={`patient-selector selected ${compact ? 'compact' : ''}`} ref={wrapperRef}>
        <div className="selected-patient">
          <i className="fas fa-user"></i>
          <div className="patient-info">
            <span className="patient-name">
              {selectedPatient.firstname} {selectedPatient.lastname}
            </span>
            <span className="patient-pnr">
              {formatPersonnummer(selectedPatient.personnummer)}
            </span>
          </div>
          {!disabled && (
            <button
              className="clear-btn"
              onClick={handleClear}
              title="Ta bort patient"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Annars visa sökfält
  return (
    <div className={`patient-selector ${compact ? 'compact' : ''}`} ref={wrapperRef}>
      <div className="search-input-wrapper">
        <i className="fas fa-search search-icon"></i>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="patient-search-input"
        />
        {loading && <i className="fas fa-spinner fa-spin loading-icon"></i>}
      </div>

      {showDropdown && (searchQuery.length >= 2 || patients.length > 0 || showAddButton) && (
        <div className="patient-dropdown">
          {loading ? (
            <div className="dropdown-loading">
              <i className="fas fa-spinner fa-spin"></i> Söker...
            </div>
          ) : patients.length === 0 && searchQuery.length >= 2 ? (
            <div className="dropdown-empty">
              Ingen patient hittad
              {showAddButton && (
                <button
                  className="add-patient-link"
                  onClick={handleOpenAddModal}
                  type="button"
                >
                  <i className="fas fa-plus"></i> Lägg till ny patient
                </button>
              )}
            </div>
          ) : patients.length === 0 ? (
            <div className="dropdown-empty">
              Skriv minst 2 tecken
            </div>
          ) : (
            <>
              {patients.map((patient) => (
                <div
                  key={patient.patient_id}
                  className="patient-option"
                  onClick={() => handleSelect(patient)}
                >
                  <span className="option-name">
                    {patient.firstname} {patient.lastname}
                  </span>
                  <span className="option-pnr">
                    {formatPersonnummer(patient.personnummer)}
                  </span>
                </div>
              ))}
              {showAddButton && (
                <div
                  className="patient-option add-new-option"
                  onClick={handleOpenAddModal}
                >
                  <span className="option-name">
                    <i className="fas fa-plus"></i> Lägg till ny patient
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modal för att lägga till ny patient */}
      {showAddModal && (
        <div className="quick-patient-overlay" onClick={handleCloseAddModal}>
          <div className="quick-patient-modal" onClick={(e) => e.stopPropagation()}>
            <div className="quick-patient-header">
              <h3><i className="fas fa-user-plus"></i> Ny patient</h3>
              <button className="close-btn" onClick={handleCloseAddModal}>&times;</button>
            </div>

            <form onSubmit={handleCreatePatient}>
              <div className="quick-patient-body">
                {addError && <div className="quick-patient-error">{addError}</div>}

                <div className="form-group">
                  <label>Personnummer *</label>
                  <input
                    type="text"
                    placeholder="YYYYMMDD-XXXX"
                    value={newPatient.personnummer}
                    onChange={(e) => setNewPatient(prev => ({ ...prev, personnummer: e.target.value }))}
                    autoFocus
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Förnamn *</label>
                    <input
                      type="text"
                      value={newPatient.firstname}
                      onChange={(e) => setNewPatient(prev => ({ ...prev, firstname: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Efternamn *</label>
                    <input
                      type="text"
                      value={newPatient.lastname}
                      onChange={(e) => setNewPatient(prev => ({ ...prev, lastname: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>E-post</label>
                    <input
                      type="email"
                      value={newPatient.email}
                      onChange={(e) => setNewPatient(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Telefon</label>
                    <input
                      type="tel"
                      value={newPatient.phone}
                      onChange={(e) => setNewPatient(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="quick-patient-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseAddModal}>
                  Avbryt
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><i className="fas fa-spinner fa-spin"></i> Skapar...</> : 'Skapa patient'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientSelector;
